import { useCallback, useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { SizeMe } from 'react-sizeme';
import { StringParam, useQueryParams } from 'use-query-params';

import G6Wrapper from 'dashboard/components/GraphCanvasV2/G6Wrapper';
import GraphCanvasContextProvider from 'dashboard/components/GraphModule/GraphCanvasContextProvider';
import GraphConfigurationProvider from 'dashboard/components/GraphModule/GraphConfigurationProvider';
import GraphDataWrapper from 'dashboard/components/GraphModule/GraphDataWrapper';
import GraphQueryingProvider from 'dashboard/components/GraphModule/GraphQueryingProvider';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { DatumTypeToken, NodeId } from 'dashboard/models/graph';
import { Backend } from 'dashboard/models/graph-backend';
import { DemoBackend } from 'dashboard/models/graph-demo-backend';
import { GraphExplorer, GraphViewTypes } from 'dashboard/models/graph-explorer';
import { GraphQueryBody } from 'dashboard/models/graph-query';
import { GraphQueryTemplateStatus } from 'dashboard/models/graph-query-template';
import {
  EmailAddressMatchType,
  ListItemBrowserFingerprint,
  ListItemDeviceFingerprint,
  ListItemEmailAddress,
  ListItemGovernmentIdNumber,
  ListItemIpAddress,
  ListItemName,
  ListItemPhoneNumber,
} from 'dashboard/models/list-item';
import { Tag } from 'dashboard/models/tag';
import { clearGraphQueryTemplate } from 'dashboard/redux/actions/graph-query-template';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadAccountTypes } from 'dashboard/redux/thunk-actions/account-types';
import { loadExporterGraph, loadGraphsAndGraph } from 'dashboard/redux/thunk-actions/graph';
import {
  loadGraphQueryTemplate,
  saveGraphQueryTemplate,
} from 'dashboard/redux/thunk-actions/graph-query-template';
import { loadGraphQueryTemplates } from 'dashboard/redux/thunk-actions/graph-query-templates';
import {
  createGraphSnapshot,
  fetchGraphSnapshot,
} from 'dashboard/redux/thunk-actions/graph-snapshot';
import { loadLists } from 'dashboard/redux/thunk-actions/list';
import { loadTags } from 'dashboard/redux/thunk-actions/tags';
import DashboardGraphExplorerView from 'dashboard/views/DashboardGraphExplorerView';
import DashboardGraphMaintenanceView from 'dashboard/views/DashboardGraphMaintenanceView';
import { SegmentClient } from 'lib/clients/SegmentClient';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import {
  BROWSER_FINGERPRINT_DATUM_TYPE,
  DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE,
  EMAIL_DATUM_TYPE,
  GOVERNMENT_ID_DATUM_TYPE,
  NAME_DATUM_TYPE,
  NETWORK_DATUM_TYPE,
  PHONE_NUMBER_DATUM_TYPE,
} from 'lib/constants/graph';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardGraphExplorerRoute = (props: PropsFromRedux) => {
  const maintenanceModeEnabled = useFeatureFlag('dashboard:graph-maintenance-mode');
  // NOTE (lewis): In an effort to maintain responsibilities, for now keep the
  // DashboardGraphExplorerRoute component restricted to interaction with redux + redux-based
  // data loading. All other instantiation should exist in the view

  const { accountTypes, accountStatuses, currentEnvironment } = props;

  // -----------------------------------------------------------------------------------------
  // Query templates
  // -----------------------------------------------------------------------------------------

  const { loading: loadingGraphQueryTemplates } = useApiLoader({
    loader: async () => {
      await props.loadGraphQueryTemplates({ status: [GraphQueryTemplateStatus.Active] });
    },
    deps: [],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure, false);
      } else {
        props.showMessage(
          'Failed to load graph, please reload to try again',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
        false,
      );
    },
  });

  const { error: saveGraphQueryTemplateError, callback: handleSaveGraphQueryTemplate } =
    useApiCallback(
      async (
        templateId: string | null,
        name: string,
        description: string,
        data: GraphQueryBody,
        saveAs: boolean,
      ) => {
        await props.saveGraphQueryTemplate(
          name,
          description,
          data.type,
          data,
          saveAs ? undefined : templateId || props.graphQueryTemplate?.id,
        );
        const displayMessage = `Successfully saved graph query template named "${name}"`;
        props.showMessage(displayMessage, MessageType.Success);
        props.loadGraphQueryTemplates();

        saveAs
          ? SegmentClient.sendSegmentTrackEvent('Graph - Save As Template')
          : SegmentClient.sendSegmentTrackEvent('Graph - Save Template');
        return true;
      },
      [props.graphQueryTemplate],
    );

  useApiErrorHandler({
    error: saveGraphQueryTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to save graph query template, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  // -----------------------------------------------------------------------------------------
  // Loading all Graphs for this organization
  // -----------------------------------------------------------------------------------------
  const { loading: loadingGraphsAndGraph } = useApiLoader({
    loader: () => {
      return props.loadGraphsAndGraph(currentEnvironment.id);
    },
    deps: [currentEnvironment],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load graph, please reload to try again', MessageType.Failure);
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  // -----------------------------------------------------------------------------------------
  // Account status loading
  // -----------------------------------------------------------------------------------------
  const { loading: loadingAccountStatuses } = useApiLoader({
    loader: () => {
      return props.loadAccountTypes();
    },
    deps: [currentEnvironment],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load graph, please reload to try again', MessageType.Failure);
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  const [urlQueryParams] = useQueryParams({
    queryTemplateId: StringParam,
    snapshotId: StringParam,
  });

  const [loadedGraphQueryTemplateFromParam, setLoadedGraphQueryTemplateFromParam] =
    useState<boolean>(false);
  useEffect(() => {
    if (!loadedGraphQueryTemplateFromParam && urlQueryParams.queryTemplateId) {
      setLoadedGraphQueryTemplateFromParam(true);
      props.loadGraphQueryTemplate(urlQueryParams.queryTemplateId);
    }
  }, [
    loadedGraphQueryTemplateFromParam,
    setLoadedGraphQueryTemplateFromParam,
    props,
    urlQueryParams,
  ]);

  const [loadedGraphSnapshotFromParam, setLoadedGraphSnapshotFromParam] = useState<boolean>(false);

  useEffect(() => {
    if (!loadedGraphSnapshotFromParam && urlQueryParams.snapshotId) {
      setLoadedGraphSnapshotFromParam(true);
      props.fetchGraphSnapshot(urlQueryParams.snapshotId);
    }
  }, [urlQueryParams.snapshotId, props.fetchGraphSnapshot, props]);

  // -----------------------------------------------------------------------------------------
  // Tags
  // -----------------------------------------------------------------------------------------

  // Preload tags (but don't wait on it) to prepare tags data for TagsModal
  const { loading: _loadingTags } = useApiLoader({
    loader: async () => {
      await props.loadTags('all');
    },
    deps: [],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load graph, please reload to try again', MessageType.Failure);
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  // Temporary refs. TODO: Remove reliance on refs by reorganizing code.
  // use ref to track g6WrapperRef since backend get's instantiated further in the tree
  const g6WrapperRef = useRef<G6Wrapper>(null);
  // use ref to track backend since backend get's instantiated further in the tree
  const backendRef = useRef<Backend>(null);
  // use ref to track graphExplorer since backend get's instantiated further in the tree
  const graphExplorerRef = useRef<GraphExplorer>(null);

  const { error: addTagsError, callback: handleUpdateTags } = useApiCallback(
    async (nodeIds: NodeId[], accountIds: string[], { tags: tagIds }: FormUpdateTagsData) => {
      if (!(backendRef.current instanceof DemoBackend)) {
        await SessionClientDashboard.postResourceSingletonAction(
          { type: ApiModel.Account },
          'bulk-add-tags',
          {
            meta: {
              account_ids: accountIds,
              tag_id: tagIds,
            },
          },
        );
      }

      const displayMessage = `Successfully tagged ${accountIds.length} accounts`;
      props.showMessage(displayMessage, MessageType.Success);
      SegmentClient.sendSegmentTrackEvent('Graph - Tag');

      // Note: We do another server fetch, because the form data doesn't give us the tag names (unfortunately). (We should fix this behavior.)
      Promise.all(
        tagIds.map((tagId) => SessionClientDashboard.getResource<Tag>(ApiEndpoint.Tag, tagId)),
      ).then((tags) => {
        graphExplorerRef.current?.demoOnlyAddTags(
          nodeIds,
          tags.map((tag) => tag.data.attributes.name),
        );
      });
    },
    [currentEnvironment, props.graph?.id],
  );

  useApiErrorHandler({
    error: addTagsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to add tags, please try again.', MessageType.Failure, false);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  // -----------------------------------------------------------------------------------------
  // Account status handlers
  // -----------------------------------------------------------------------------------------
  const { error: updateAccountStatusesError, callback: handleUpdateAccountStatuses } =
    useApiCallback(
      async (_nodeIds: NodeId[], accountIds: string[], accountStatusId: string) => {
        await SessionClientDashboard.postResourceSingletonAction(
          { type: ApiModel.Account },
          'bulk-update-account-status',
          {
            meta: {
              account_ids: accountIds,
              account_status_id: accountStatusId,
            },
          },
        );

        const displayMessage = `Successfully updated status of ${accountIds.length} accounts`;
        props.showMessage(displayMessage, MessageType.Success);
      },
      [currentEnvironment, props.graph?.id],
    );

  useApiErrorHandler({
    error: updateAccountStatusesError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to update status for accounts, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  // -----------------------------------------------------------------------------------------
  // Graph Snapshots
  // -----------------------------------------------------------------------------------------
  const {
    error: createGraphSnapshotError,
    callback: handleCreateGraphSnapshot,
    loading: loadingCreateGraphSnapshot,
  } = useApiCallback(
    async (graphId: string | undefined, graphQueryId: string, payload: any) => {
      const createdSnapshot = await props.createGraphSnapshot(graphId, graphQueryId, payload);
      if (createdSnapshot) {
        props.showMessage(`Snapshot created successfully`, MessageType.Success);
        SegmentClient.sendSegmentTrackEvent('Graph - Create Snapshot');
        const snapshotUrl = `/dashboard/explorer?snapshotId=${createdSnapshot?.id}`;
        return `${window.location.origin}${snapshotUrl}`;
      }
    },
    [props.createGraphSnapshot],
  );

  useApiErrorHandler({
    error: createGraphSnapshotError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to create graph snapshot, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });
  // -----------------------------------------------------------------------------------------
  // Add Property to List Action
  // -----------------------------------------------------------------------------------------

  const { loading: loadingLists } = useApiLoader({
    loader: () =>
      props.loadLists({
        currentEnvironment: currentEnvironment,
        filters: {},
        sortParams: { sort: '-', shouldSortDescending: false },
      }),
    deps: [currentEnvironment],
    skip: !usePermission(Permission.ListView),
    onError: () => showMessage('Failed to load lists.', MessageType.Failure),
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const addListItem = useCallback(
    async (
      listId: string,
      datumTypeToken: DatumTypeToken,
      listItemValue: string,
      idClass?: GovernmentIdClass, // TODO(jes): Currently this is always undefined. Would be useful to populate from hydration in the future
    ) => {
      try {
        switch (datumTypeToken) {
          case GOVERNMENT_ID_DATUM_TYPE.token:
            await SessionClientDashboard.postResourceCreate<ListItemGovernmentIdNumber>({
              attributes: {
                'list-id': listId,
                'id-class': idClass,
                'id-number': listItemValue,
                'environment-id': currentEnvironment.id,
              },
              type: ApiModel.ListItemGovernmentIdNumber,
            });
            break;
          case NETWORK_DATUM_TYPE.token:
            await SessionClientDashboard.postResourceCreate<ListItemIpAddress>({
              attributes: {
                'list-id': listId,
                value: listItemValue,
                'environment-id': currentEnvironment.id,
              },
              type: ApiModel.ListItemIpAddress,
            });
            break;
          case NAME_DATUM_TYPE.token:
            await SessionClientDashboard.postResourceCreate<ListItemName>({
              attributes: {
                'list-id': listId,
                'name-first': JSON.parse(listItemValue)['name-first'],
                'name-last': JSON.parse(listItemValue)['name-last'],
                'environment-id': currentEnvironment.id,
              },
              type: ApiModel.ListItemName,
            });
            break;
          case PHONE_NUMBER_DATUM_TYPE.token:
            await SessionClientDashboard.postResourceCreate<ListItemPhoneNumber>({
              attributes: {
                'list-id': listId,
                value: listItemValue,
                'environment-id': currentEnvironment.id,
              },
              type: ApiModel.ListItemPhoneNumber,
            });
            break;
          case EMAIL_DATUM_TYPE.token:
            await SessionClientDashboard.postResourceCreate<ListItemEmailAddress>({
              attributes: {
                'list-id': listId,
                'match-type': EmailAddressMatchType.Email,
                value: listItemValue,
                'environment-id': currentEnvironment.id,
              },
              type: ApiModel.ListItemEmailAddress,
            });
            break;
          case BROWSER_FINGERPRINT_DATUM_TYPE.token:
            await SessionClientDashboard.postResourceCreate<ListItemBrowserFingerprint>({
              attributes: {
                'list-id': listId,
                value: listItemValue,
                'environment-id': currentEnvironment.id,
              },
              type: ApiModel.ListItemBrowserFingerprint,
            });
            break;
          case DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.token:
            await SessionClientDashboard.postResourceCreate<ListItemDeviceFingerprint>({
              attributes: {
                'list-id': listId,
                value: listItemValue,
                'environment-id': currentEnvironment.id,
              },
              type: ApiModel.ListItemDeviceFingerprint,
            });
            break;
          default:
            throw new Error(`Unsupported list type`);
        }

        props.showMessage('Successfully added item to list.', MessageType.Success);
      } catch (err) {
        // NOTE: we should be deliberate about checking error codes. See
        // https://www.notion.so/withpersona/Error-serialization-282f3c016ee7450eabe42dd273762cf0
        if (err instanceof ApiResponseError && err.status === 409) {
          props.showMessage('Item already exists in this list.', MessageType.Failure);
        } else {
          props.showMessage('Failed to add item to list, please try again.', MessageType.Failure);
        }

        throw err;
      }
    },
    [],
  );

  if (maintenanceModeEnabled) {
    return <DashboardGraphMaintenanceView />;
  }

  return (
    <GraphDataWrapper
      // TODO: Once GraphDataWrapper is able to fetch it's own data, it is worth hoisting into the parent
      // component that connects to graph contexts
      accountStatuses={accountStatuses}
      accountTypes={accountTypes}
      currentEnvironment={props.currentEnvironment}
      currentUser={props.currentUser}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      graph={props.graph}
      graphQueryTemplates={props.graphQueryTemplates}
    >
      <GraphCanvasContextProvider
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        graph={props.graph}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        viewTypeSwitchEnabled={props.featureFlags['dashboard:graph-pairwise-similarity-nodes']}
        viewType={GraphViewTypes.Parsed}
      >
        <GraphConfigurationProvider>
          <GraphQueryingProvider showMessage={props.showMessage}>
            <SizeMe monitorWidth>
              {({ size }) => (
                <DashboardGraphExplorerView
                  accountTypes={accountTypes}
                  nodeFunctionsConfigs={props.graph?.attributes['node-functions-configs'] || []}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  g6WrapperRef={g6WrapperRef}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  backendRef={backendRef}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  graphExplorerRef={graphExplorerRef}
                  graphQueryTemplate={props.graphQueryTemplate}
                  graphSnapshot={props.graphSnapshot}
                  clearGraphQueryTemplate={props.clearGraphQueryTemplate}
                  handleLoadGraphQueryTemplate={props.loadGraphQueryTemplate}
                  handleAddToList={addListItem}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  handleSaveGraphQueryTemplate={handleSaveGraphQueryTemplate}
                  handleCreateGraphSnapshot={handleCreateGraphSnapshot}
                  handleUpdateAccountStatuses={handleUpdateAccountStatuses}
                  handleUpdateTags={handleUpdateTags}
                  lists={props.lists}
                  loadingLists={loadingLists}
                  loadingAccountStatuses={loadingAccountStatuses}
                  loadingGraphsAndGraph={loadingGraphsAndGraph}
                  loadingGraphQueryTemplates={loadingGraphQueryTemplates}
                  loadingCreateGraphSnapshot={loadingCreateGraphSnapshot}
                  showMessage={props.showMessage}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  viewWidth={size.width}
                />
              )}
            </SizeMe>
          </GraphQueryingProvider>
        </GraphConfigurationProvider>
      </GraphCanvasContextProvider>
    </GraphDataWrapper>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountTypes: state.accountTypes.accountTypes,
    accountStatuses: state.accountTypes.accountStatuses,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    currentUser: state.currentUser.currentUser,
    lists: state.lists.lists,
    graph: state.graph.graph,
    graphs: state.graph.graphs,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    graphQueryTemplate: state.graphQueryTemplate.graphQueryTemplate,
    graphQueryTemplates: state.graphQueryTemplates.graphQueryTemplates,
    graphSnapshot: state.graphSnapshots.graphSnapshot,
    organization: state.organization.organization,
    tags: state.tags.tags.all,
    featureFlags: state.featureFlags.featureFlags,
  };
};

const mapDispatchToProps = {
  clearGraphQueryTemplate,
  createGraphSnapshot,
  fetchGraphSnapshot,
  loadAccountTypes,
  loadExporterGraph,
  loadGraphsAndGraph,
  loadGraphQueryTemplates,
  loadGraphQueryTemplate,
  loadLists,
  loadTags,
  saveGraphQueryTemplate,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedDashboardGraphExplorerRoute = connector(DashboardGraphExplorerRoute);

export default ConnectedDashboardGraphExplorerRoute;
